/**
 * Package management page with list, search, and CRUD operations.
 */

import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Space, message, Card, Modal, Drawer } from 'antd';
import { PlusOutlined, HistoryOutlined } from '@ant-design/icons';

import { usePackageStore } from '@/store/packageStore';
import { useConfigStore } from '@/store/configStore';
import { useAuth } from '@/hooks/useAuth';
import { PackageList } from '@/components/packages/PackageList';
import { PackageForm, PackageFormData } from '@/components/packages/PackageForm';
import { SearchBar, SearchFilters } from '@/components/common/SearchBar';
import { OperationLog } from '@/components/logs/OperationLog';
import { getPackageLogs } from '@/api/logs';
import { createPackage, updatePackage, deletePackage, getPackage, getPreviousGitSources, failPackageFileUpload, failReleaseNoteUpload, failTestReportUpload } from '@/api/packages';
import { reportUploadEvent } from '@/api/storage';
import { getStuckThresholdMs } from '@/utils/uploadThreshold';
import { useUploadProgressStore } from '@/store/uploadProgressStore';
import { reportPackageUploadRecord } from '@/utils/uploadRecordUtils';
import type { BSPPackage, BSPPackageDetail, OperationLog as OperationLogType, PreviousGitSource } from '@/types';
import { useT } from '@/i18n';

const PackageManagement = () => {
  const { t } = useT();
  const { projectId } = useParams<{ projectId: string }>();
  const { user, isInternal, isAdmin } = useAuth();
  const { packages, pagination, isLoading, fetchPackages, setPackageUploadStatus } = usePackageStore();
  const { projects, fileTypes, fetchProjects, fetchFileTypes } = useConfigStore();

  const [searchFilters, setSearchFilters] = useState<SearchFilters>({});
  const [selectedPackage, setSelectedPackage] = useState<BSPPackage | null>(null);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [isLogVisible, setIsLogVisible] = useState(false);
  const [logs, setLogs] = useState<OperationLogType[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [previousGitSources, setPreviousGitSources] = useState<PreviousGitSource[]>([]);
  const uploadStartedAtRef = useRef<string | null>(null);
  const snapshotFetchedRef = useRef(false);  // kept for compatibility, unused

  const currentProject = projects.find((p) => p.id === Number(projectId));

  // Use only supplier file types for package management
  const availableFileTypes = useMemo(() => {
    if (!currentProject) return fileTypes;
    const supplierTypes = currentProject.allowed_file_types || [];
    return supplierTypes.length > 0 ? supplierTypes : fileTypes;
  }, [currentProject, fileTypes]);

  useEffect(() => {
    fetchProjects();
    fetchFileTypes();
  }, [fetchProjects, fetchFileTypes]);

  useEffect(() => {
    fetchPackages({
      project_id: projectId ? Number(projectId) : undefined,
      page: 1,
      page_size: 20,
    }).then(() => failStuckFiles());
  }, [projectId, fetchPackages]);

  // 对 store 中无活跃进度的 uploading/queued 文件标记为 failed
  // 后端 file_upload_complete 接口会自动聚合包状态
  const failStuckFiles = useCallback(async () => {
    snapshotFetchedRef.current = true; // no-op, kept for ref compatibility
    const now = Date.now();
    const uploadingPkgs = usePackageStore.getState().packages.filter(p => p.upload_status === 'uploading');
    await Promise.allSettled(
      uploadingPkgs.map(async (pkg) => {
        const detail = await getPackage(pkg.id).catch(() => null);
        if (!detail) return;
        const progressStore = useUploadProgressStore.getState();
        const stuckFiles = (detail.files || []).filter(f => {
          if (f.upload_status !== 'uploading' && f.upload_status !== 'queued') return false;
          const entry = progressStore.getProgress(`package-${pkg.id}-file-${f.id}`);
          if (entry) {
            // 本地有明确状态：failed → 网络断开时failPackageFileUpload未能通知后端，立即补发；仍在上传中 → 跳过
            if (entry.status === 'failed') {
              console.warn('[failStuckFiles] pkg_id=%d file_id=%d file_name=%s: local store=failed, sending fail to backend', pkg.id, f.id, f.file_name);
              reportUploadEvent('fail_stuck_file', { level: 'warn', deliverable: 'package', deliverable_id: pkg.id, file_id: f.id, file_name: f.file_name, detail: { reason: 'local_store_failed', file_status: f.upload_status } });
              return true;
            }
            return false;
          }
          // 本地无记录（刷新页面 / 其他标签页）→ 保留时间阈值兜底
          const updatedAt = f.updated_at ? new Date(f.updated_at).getTime() : Date.now();
          const staleSec = Math.round((now - updatedAt) / 1000);
          const thresholdMs = getStuckThresholdMs({
            status: f.upload_status,
            pendingObjectKey: f.pending_object_key,
          });
          const isStuck = (now - updatedAt) > thresholdMs;
          console.warn(
            '[failStuckFiles] pkg_id=%d file_id=%d file_name=%s status=%s: no local store entry, updated_at=%s stale=%ds threshold=%ds → %s',
            pkg.id, f.id, f.file_name, f.upload_status, f.updated_at, staleSec, Math.round(thresholdMs / 1000), isStuck ? 'MARKING FAILED' : 'skip',
          );
          if (isStuck) {
            reportUploadEvent('fail_stuck_file', { level: 'warn', deliverable: 'package', deliverable_id: pkg.id, file_id: f.id, file_name: f.file_name, detail: { reason: 'heartbeat_timeout', file_status: f.upload_status, stale_seconds: staleSec, threshold_seconds: Math.round(thresholdMs / 1000), last_updated_at: f.updated_at } });
          }
          return isStuck;
        });
        const packageUpdatedAt = detail.updated_at ? new Date(detail.updated_at).getTime() : Date.now();
        // If any file is still actively uploading/queued and recently heartbeated, RN/TR are
        // legitimately waiting in queue — don't mark them as stuck.
        const hasActiveFile = (detail.files || []).some(f => {
          if (f.upload_status !== 'uploading' && f.upload_status !== 'queued') return false;
          const updatedAt = f.updated_at ? new Date(f.updated_at).getTime() : Date.now();
          return (now - updatedAt) <= getStuckThresholdMs({
            status: f.upload_status,
            pendingObjectKey: f.pending_object_key,
          });
        });
        const PACKAGE_STUCK_THRESHOLD_MS = 5 * 60 * 1000;
        const rnEntry = progressStore.getProgress(`package-${pkg.id}-doc-release_note`);
        const rnStuck = !!detail.release_note_name && !detail.release_note_url
          && (!rnEntry || rnEntry.status === 'failed')
          && (now - packageUpdatedAt) > PACKAGE_STUCK_THRESHOLD_MS
          && !hasActiveFile;
        const trPrefix = `package-${pkg.id}-doc-test_report`;
        const trActive = Object.entries(progressStore.progress).some(
          ([key, entry]) => (key === trPrefix || key.startsWith(`${trPrefix}-`))
            && !!entry && entry.status !== 'done' && entry.status !== 'failed',
        );
        const trEntry = progressStore.getProgress(trPrefix);
        const trStuck = !!detail.test_report_name && !detail.test_report_url
          && !trActive
          && (!trEntry || trEntry.status === 'failed')
          && (now - packageUpdatedAt) > PACKAGE_STUCK_THRESHOLD_MS
          && !hasActiveFile;
        if (stuckFiles.length > 0 || rnStuck || trStuck) {
          console.warn('[failStuckFiles] pkg_id=%d: marking stuck items — files=%o rnStuck=%s trStuck=%s', pkg.id, stuckFiles.map(f => f.id), rnStuck, trStuck);
        }
        await Promise.allSettled([
          ...stuckFiles.map(f => failPackageFileUpload(pkg.id, f.id)),
          ...(rnStuck ? [failReleaseNoteUpload(pkg.id)] : []),
          ...(trStuck ? [failTestReportUpload(pkg.id)] : []),
        ]);
      })
    );
  }, []);

  // Poll every 5s when any package is uploading
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    const hasUploading = packages.some(p => p.upload_status === 'uploading');
    if (hasUploading && !pollingRef.current) {
      pollingRef.current = setInterval(() => {
        fetchPackages({
          project_id: projectId ? Number(projectId) : undefined,
          page: 1,
          page_size: 20,
        }).then(() => failStuckFiles());
      }, 5000);
    } else if (!hasUploading && pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [packages, projectId, fetchPackages, failStuckFiles]);

  const handleSearch = useCallback(
    (filters: SearchFilters) => {
      setSearchFilters(filters);
      fetchPackages({
        project_id: projectId ? Number(projectId) : undefined,
        file_type: filters.file_type || undefined,
        search: filters.search || undefined,
        page: 1,
        page_size: 20,
      });
    },
    [projectId, fetchPackages]
  );

  const handlePageChange = useCallback(
    (page: number, pageSize: number) => {
      fetchPackages({
        project_id: projectId ? Number(projectId) : undefined,
        ...searchFilters,
        page,
        page_size: pageSize,
      });
    },
    [projectId, searchFilters, fetchPackages]
  );

  const handleCreate = async () => {
    setSelectedPackage(null);
    setIsEditMode(false);
    uploadStartedAtRef.current = new Date().toISOString();

    if (projectId) {
      try {
        const sources = await getPreviousGitSources(Number(projectId));
        setPreviousGitSources(sources);
      } catch {
        setPreviousGitSources([]);
      }
    } else {
      setPreviousGitSources([]);
    }

    setIsFormVisible(true);
  };

  const handleEdit = async (pkg: BSPPackage) => {
    try {
      const fullPackage = await getPackage(pkg.id);
      setSelectedPackage(fullPackage);
      setIsEditMode(true);
      uploadStartedAtRef.current = new Date().toISOString();

      if (projectId) {
        try {
          const sources = await getPreviousGitSources(Number(projectId), pkg.id);
          setPreviousGitSources(sources);
        } catch {
          setPreviousGitSources([]);
        }
      } else {
        setPreviousGitSources([]);
      }

      setIsFormVisible(true);
    } catch {
      message.error(t('获取包详情失败'));
    }
  };

  const handleViewLogs = async (pkg: BSPPackage) => {
    setSelectedPackage(pkg);
    setIsLogVisible(true);
    setLogsLoading(true);
    try {
      const response = await getPackageLogs(pkg.id);
      setLogs(response.results);
    } catch {
      message.error(t('获取操作日志失败'));
    } finally {
      setLogsLoading(false);
    }
  };

  const handleDelete = async (pkg: BSPPackage) => {
    try {
      await deletePackage(pkg.id);
      message.success(t('删除成功'));
      fetchPackages({
        project_id: projectId ? Number(projectId) : undefined,
        ...searchFilters,
        page: pagination.page,
        page_size: pagination.pageSize,
      });
    } catch {
      message.error(t('删除失败'));
    }
  };

  const handleFormSubmit = async (data: PackageFormData) => {
    setFormLoading(true);

    try {
      let result;
      if (isEditMode && selectedPackage) {
        result = await updatePackage(selectedPackage.id, data);
        if (result?.id) {
          setPackageUploadStatus(result.id, 'uploading');
        }
      } else {
        if (!projectId) {
          message.error(t('请选择项目'));
          setFormLoading(false);
          return result as any;
        }
        result = await createPackage({ ...data, project_id: Number(projectId) });
      }
      setIsFormVisible(false);

      // Report upload record and keep id for PackageForm to write back when upload completes
      let uploadRecordId: number | null = null;
      let categorizedFiles: { deleted: any[]; existing: any[] } | undefined;
      if (result?.id) {
        const beforePackage = isEditMode && selectedPackage
          ? (selectedPackage as BSPPackageDetail)
          : {} as BSPPackageDetail;
        const recordResult = await reportPackageUploadRecord(
          result,
          data,
          beforePackage,
          Number(projectId),
          fileTypes,
          uploadStartedAtRef.current,
        );
        uploadRecordId = recordResult?.recordId ?? null;
        if (recordResult) {
          categorizedFiles = { deleted: recordResult.deletedItems, existing: recordResult.existingItems };
        }
      }

      await fetchPackages({
        project_id: projectId ? Number(projectId) : undefined,
        page: 1,
        page_size: 20,
      });
      // fetch 完成后再次覆盖，防止 fetch 用 DB 旧值覆盖乐观更新
      if (isEditMode && result?.id) {
        setPackageUploadStatus(result.id, 'uploading');
      }

      // 把 uploadRecordId 和 categorizedFiles 附加到返回值，供 PackageForm 在后台上传完成时直接回写
      return { ...(result as object), uploadRecordId: uploadRecordId ?? null, categorizedFiles } as any;
    } catch (error) {
      const err = error as { response?: { status: number; data?: any } };

      // DEBUG: Log full error response
      console.error('[PackageManagement:Submit:Error] Full error response:', {
        status: err.response?.status,
        data: err.response?.data,
        dataType: typeof err.response?.data,
        dataKeys: err.response?.data ? Object.keys(err.response.data) : [],
        dataJSON: err.response?.data ? JSON.stringify(err.response.data) : null,
      });

      if (err.response?.status === 409) {
        Modal.confirm({
          title: t('数据冲突'),
          content: t('该记录已被其他用户修改。是否强制覆盖？'),
          okText: t('强制覆盖'),
          cancelText: t('放弃修改'),
          onOk: async () => {
            try {
              const retryResult = await updatePackage(selectedPackage!.id, { ...data, force: true });
              message.success(t('更新成功'));
              setIsFormVisible(false);
              if (selectedPackage) {
                await reportPackageUploadRecord(
                  retryResult,
                  data,
                  selectedPackage as BSPPackageDetail,
                  Number(projectId),
                  fileTypes,
                  uploadStartedAtRef.current,
                );
              }
              fetchPackages({
                project_id: projectId ? Number(projectId) : undefined,
                page: 1,
                page_size: 20,
              });
            } catch {
              message.error(t('更新失败'));
            }
          },
        });
      } else {
        // Try to extract error message from various formats
        let errorMsg = t('操作失败');

        if (err.response?.data) {
          if (typeof err.response.data === 'string') {
            errorMsg = err.response.data;
          } else if (err.response.data.detail) {
            errorMsg = err.response.data.detail;
          } else if (err.response.data.error) {
            // Prioritize 'error' field over 'message' because 'message' might be '{}'
            errorMsg = err.response.data.error;
          } else if (err.response.data.message && err.response.data.message !== '{}') {
            // Only use 'message' if it's not '{}'
            errorMsg = err.response.data.message;
          } else {
            // If it's an object with validation errors, format them
            const dataStr = JSON.stringify(err.response.data);
            errorMsg = dataStr !== '{}' ? dataStr : t('操作失败');
          }
        }

        message.error(errorMsg);
      }
      throw error;
    } finally {
      setFormLoading(false);
    }
  };

  const canCreate = user && (isInternal || isAdmin || user.user_type === 'external');

  return (
    <div className="p-4">
      <Card
        title={currentProject ? t('{name} - BSP 底软包', { name: currentProject.name }) : t('BSP 底软包管理')}
        extra={
          canCreate && (
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              {t('新建')}
            </Button>
          )
        }
      >
        <SearchBar
          projects={projects.map((p) => ({ id: p.id, name: p.name }))}
          fileTypes={fileTypes.map((ft) => ({ id: ft.id, name: ft.name }))}
          onSearch={handleSearch}
          loading={isLoading}
        />

        <PackageList
          packages={packages}
          loading={isLoading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.pageSize,
            total: pagination.count,
          }}
          projectId={projectId ? Number(projectId) : undefined}
          onEdit={handleEdit}
          onDelete={isAdmin ? handleDelete : undefined}
          onViewLogs={handleViewLogs}
          onPageChange={handlePageChange}
        />
      </Card>

      {/* Package Form Drawer */}
      <Drawer
        title={isEditMode ? t('编辑底软包') : t('新建底软包')}
        placement="right"
        styles={{ wrapper: { width: 1200 } }}
        open={isFormVisible}
        onClose={() => setIsFormVisible(false)}
        destroyOnHidden
        mask={{ closable: false }}
      >
        <PackageForm
          initialData={selectedPackage as BSPPackageDetail | null}
          projects={projects}
          projectId={projectId ? Number(projectId) : undefined}
          fileTypes={availableFileTypes}
          onSubmit={handleFormSubmit}
          onCancel={() => setIsFormVisible(false)}
          loading={formLoading}
          isEdit={isEditMode}
          previousGitSources={previousGitSources}
        />
      </Drawer>

      {/* Operation Log Modal */}
      <Modal
        title={
          <Space>
            <HistoryOutlined />
            {t('操作日志 - {name}', { name: selectedPackage?.name ?? '' })}
          </Space>
        }
        open={isLogVisible}
        onCancel={() => setIsLogVisible(false)}
        footer={null}
        width={900}
        destroyOnHidden
      >
        <OperationLog logs={logs} loading={logsLoading} />
      </Modal>
    </div>
  );
};

export default PackageManagement;
